"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValueEvent,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";

type Props = {
  locale: "fr" | "en";

  // Phase 1 — hero principal
  phase1Eyebrow: string;
  phase1TitleLine1: string;
  phase1TitleLine2: string;
  phase1CtaPrimary: string;
  phase1CtaSecondary: string;
  hrefPrimary: string;
  hrefSecondary: string;

  // Phase 2 — prose centrale
  phase2Eyebrow: string;
  phase2Paragraph: string;

  // Phase 3 — statement final
  phase3Eyebrow: string;
  phase3TitleLine1: string;
  phase3TitleLine2: string;

  videoSrc: string; // /hero/hero-scrub.mp4 (encodage all-keyframes)
  posterSrc: string;
};

export default function ParallaxVideoHero({
  locale,
  phase1Eyebrow,
  phase1TitleLine1,
  phase1TitleLine2,
  phase1CtaPrimary,
  phase1CtaSecondary,
  hrefPrimary,
  hrefSecondary,
  phase2Eyebrow,
  phase2Paragraph,
  phase3Eyebrow,
  phase3TitleLine1,
  phase3TitleLine2,
  videoSrc,
  posterSrc,
}: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const prefersReduced = useReducedMotion();
  const [videoReady, setVideoReady] = useState(false);
  // Phase active (1/2/3) → utilisée pour `inert` afin de désactiver les phases inactives
  // (sinon les enfants des phases invisibles capturent les clics même si parent pointer-events: none)
  const [activePhase, setActivePhase] = useState<1 | 2 | 3>(1);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // ── CŒUR DU SCROLL-SCRUB + détection de la phase active ──
  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    // 1) Scrub de la vidéo
    const video = videoRef.current;
    if (video && videoReady && !prefersReduced) {
      const duration = video.duration;
      if (duration && !isNaN(duration)) {
        const t = Math.max(0, Math.min(1, progress));
        video.currentTime = t * duration;
      }
    }
    // 2) Phase active : sur les boundaries du fade entre phases
    const next: 1 | 2 | 3 = progress < 0.34 ? 1 : progress < 0.67 ? 2 : 3;
    setActivePhase((prev) => (prev === next ? prev : next));
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onReady = () => setVideoReady(true);
    if (video.readyState >= 1) {
      setVideoReady(true);
    } else {
      video.addEventListener("loadedmetadata", onReady);
    }
    return () => video.removeEventListener("loadedmetadata", onReady);
  }, []);

  // ── 3 PHASES DE TEXTE qui apparaissent par-dessus la vidéo scrub ──
  // Phase 1 : 0 → 0.32 (visible 0.06 → 0.28, fade aux extrémités)
  const phase1Opacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.28, 0.35],
    [0, 1, 1, 0]
  );
  const phase1Y = useTransform(scrollYProgress, [0, 0.35], [30, -40]);

  // Phase 2 : 0.33 → 0.65
  const phase2Opacity = useTransform(
    scrollYProgress,
    [0.33, 0.4, 0.6, 0.68],
    [0, 1, 1, 0]
  );
  const phase2Y = useTransform(scrollYProgress, [0.33, 0.68], [30, -40]);

  // Phase 3 : 0.66 → 1
  const phase3Opacity = useTransform(
    scrollYProgress,
    [0.66, 0.74, 0.94, 1],
    [0, 1, 1, 0]
  );
  const phase3Y = useTransform(scrollYProgress, [0.66, 1], [40, -20]);

  // Hint scroll : visible seulement au tout début
  const hintOpacity = useTransform(scrollYProgress, [0, 0.04], [1, 0]);

  return (
    <section
      ref={sectionRef}
      className="relative bg-black"
      aria-label={locale === "fr" ? "Hero Welconcept" : "Welconcept hero"}
      style={{ height: "300vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* ── VIDÉO SCRUB — pilotée par le scroll ──
            preload="auto" pour bufferiser tout dès l'arrivée
            Pas d'autoplay : le playhead est contrôlé par useMotionValueEvent */}
        <video
          ref={videoRef}
          src={videoSrc}
          poster={posterSrc}
          muted
          playsInline
          preload="auto"
          // Fallback : si JS désactivé ou prefers-reduced-motion, on laisse jouer en boucle
          autoPlay={prefersReduced}
          loop={prefersReduced}
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* Overlays luxe pour lisibilité du texte */}
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-black/20" />

        {/* ── PHASE 1 — Hero principal ── */}
        <motion.div
          style={{ opacity: phase1Opacity, y: phase1Y }}
          inert={activePhase !== 1}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center will-change-transform"
        >
          <p className="font-body font-light uppercase tracking-[0.4em] text-white/70 text-[11px] md:text-xs">
            {phase1Eyebrow}
          </p>
          <h1 className="mt-6 font-heading text-5xl md:text-7xl lg:text-8xl leading-[1.05] font-light tracking-tight text-white">
            {phase1TitleLine1}
            <br />
            <span className="italic">{phase1TitleLine2}</span>
          </h1>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            <Link
              href={hrefPrimary}
              className="inline-flex rounded-full bg-white px-7 py-3 text-xs tracking-[0.18em] uppercase text-black hover:opacity-90 transition-opacity"
            >
              {phase1CtaPrimary}
            </Link>
            <Link
              href={hrefSecondary}
              className="inline-flex rounded-full border border-white/25 bg-white/5 px-7 py-3 text-xs tracking-[0.18em] uppercase text-white hover:bg-white/10 transition-colors"
            >
              {phase1CtaSecondary}
            </Link>
          </div>
        </motion.div>

        {/* ── PHASE 2 — Prose centrale ── */}
        <motion.div
          style={{ opacity: phase2Opacity, y: phase2Y }}
          inert={activePhase !== 2}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center px-8 text-center will-change-transform"
        >
          <p className="font-body font-light uppercase tracking-[0.4em] text-white/65 text-[11px] md:text-xs">
            {phase2Eyebrow}
          </p>
          <p className="mt-10 max-w-3xl font-heading text-2xl md:text-4xl lg:text-5xl leading-[1.3] font-light text-white/95">
            {phase2Paragraph}
          </p>
        </motion.div>

        {/* ── PHASE 3 — Statement final ── */}
        <motion.div
          style={{ opacity: phase3Opacity, y: phase3Y }}
          inert={activePhase !== 3}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center will-change-transform"
        >
          <p className="font-body font-light uppercase tracking-[0.45em] text-white/75 text-[11px] md:text-xs">
            {phase3Eyebrow}
          </p>
          <h2 className="mt-8 font-heading font-light text-white leading-[0.95]">
            <span className="block text-3xl md:text-5xl lg:text-6xl">
              {phase3TitleLine1}
            </span>
            <span className="block italic text-7xl md:text-[9rem] lg:text-[11rem] mt-2">
              {phase3TitleLine2}
            </span>
          </h2>
        </motion.div>

        {/* Hint scroll */}
        <motion.div
          style={{ opacity: hintOpacity }}
          className="pointer-events-none absolute inset-x-0 bottom-10 z-20 flex justify-center"
        >
          <motion.div
            animate={prefersReduced ? { y: 0 } : { y: [0, 8, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="text-[10px] tracking-[0.32em] uppercase text-white/75 font-body"
          >
            {locale === "fr" ? "Scrollez" : "Scroll"} ↓
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
